import { api } from "@/api/axios.ts";
import { ApiFieldsSort, ApiTableFieldSort } from "@/queries/use-custom-fields.query.ts";
import { ApiOperationResult } from "@/types/common.types.ts";
import {
  ApiCFEmployeeTableRow,
  ApiCFTab,
  ApiCFTableRowFilter,
  ApiCFTabSortItem,
  ApiField,
  ApiFieldDefaultValuePayload,
  ApiFieldOptionsPayload,
  ApiFieldPayload,
  ApiSystemFieldPayload,
  ApiSection,
  ApiCFEntityTypes,
  ApiSectionPayload,
  ApiTable,
  ApiTableFieldPayload,
  ApiTablePayload,
  ApiCFTabType,
  ApiCFEntityConfig,
} from "@/types/custom-field.types.ts";
import { ApiPagedData } from "@skailer-com/uikit/core";

export const apiCustomFields = {
  getEntityConfig: async (entityType: ApiCFEntityTypes) => {
    return api.get<ApiCFEntityConfig>(`/api/v1/custom-fields/config?entity_type=${entityType}`);
  },
  getSectionsByType: async function (type: ApiCFEntityTypes) {
    return api.get<ApiSection[]>(`/api/v1/custom-fields/sections?entity_type=${type}`);
  },
  createSection: async (payload: ApiSectionPayload) => {
    return api.post<ApiSection>("/api/v1/custom-fields/sections", payload);
  },
  updateSection: async (id: number, payload: ApiSectionPayload) => {
    return api.put<ApiSection>(`/api/v1/custom-fields/sections/${id}`, payload);
  },
  getTabsByEntityType: async (entityType: ApiCFEntityTypes) => {
    return api.get<ApiCFTab[]>(`/api/v1/custom-fields/tabs?entity_type=${entityType}`);
  },
  updateTabItemsSort: async (
    type: ApiCFTabType,
    entityType: ApiCFEntityTypes,
    items: ApiCFTabSortItem[]
  ) => {
    return api.put<ApiOperationResult>(`/api/v1/custom-fields/tabs/${type}/items/sort`, {
      items,
      entity_type: entityType,
    });
  },
  deleteSection: async (id: number) => {
    return api.delete<ApiOperationResult>(`/api/v1/custom-fields/sections/${id}`);
  },
  getFieldsByType: async function (type: ApiCFEntityTypes) {
    return api.get<ApiField[]>(`/api/v1/custom-fields/fields?entity_type=${type}`);
  },
  updateFieldsSort: async (items: ApiFieldsSort[]) => {
    return api.put<ApiField[]>(`/api/v1/custom-fields/fields/sort`, { items });
  },
  createField: async (payload: ApiFieldPayload) => {
    return api.post<ApiField>("/api/v1/custom-fields/fields", payload);
  },
  check: async (title: string, entityType: ApiCFEntityTypes, field_id?: number) => {
    return api.post<ApiOperationResult>("/api/v1/custom-fields/fields/check", {
      field_id,
      title,
      entity_type: entityType,
    });
  },
  updateField: async (
    id: number,
    payload: ApiFieldPayload | ApiSystemFieldPayload | ApiFieldOptionsPayload
  ) => {
    return api.put<ApiField>(`/api/v1/custom-fields/fields/${id}`, payload);
  },
  deleteField: async (id: number) => {
    return api.delete<ApiOperationResult>(`/api/v1/custom-fields/fields/${id}`);
  },
  updateDefaultValue: async (id: number, payload: ApiFieldDefaultValuePayload) => {
    return api.put<ApiField>(`/api/v1/custom-fields/fields/${id}/default-value`, payload);
  },
  deleteDefaultValue: async (id: number) => {
    return api.delete<ApiField>(`/api/v1/custom-fields/fields/${id}/default-value`);
  },
  getAllTables: async function () {
    return api.get<ApiTable[]>(`/api/v1/custom-fields/tables`);
  },
  createTable: async (payload: ApiTablePayload) => {
    return api.post<ApiTable>("/api/v1/custom-fields/tables", payload);
  },
  updateTable: async (id: number, payload: ApiTablePayload) => {
    return api.put<ApiTable>(`/api/v1/custom-fields/tables/${id}`, payload);
  },
  deleteTable: async (id: number) => {
    return api.delete<ApiOperationResult>(`/api/v1/custom-fields/tables/${id}`);
  },
  checkTable: async (title: string, field_id?: number) => {
    return api.post<ApiOperationResult>("/api/v1/custom-fields/tables/check", {
      field_id,
      title,
    });
  },
  getFieldsByTableId: async function (tableId: number) {
    return api.get<ApiField[]>(`/api/v1/custom-fields/tables/${tableId}/fields`);
  },
  updateTableFieldsSort: async (tableId: number, payload: ApiTableFieldSort) => {
    return api.put<ApiOperationResult>(
      `/api/v1/custom-fields/tables/${tableId}/fields/sort`,
      payload
    );
  },
  createTableField: async (tableId: number, payload: ApiTableFieldPayload) => {
    return api.post<ApiField>(`/api/v1/custom-fields/tables/${tableId}/fields`, payload);
  },
  updateTableField: async (
    tableId: number,
    fieldId: number,
    payload: ApiTableFieldPayload | ApiSystemFieldPayload
  ) => {
    return api.put<ApiField>(`/api/v1/custom-fields/tables/${tableId}/fields/${fieldId}`, payload);
  },
  deleteTableField: async (tableId: number, fieldId: number) => {
    return api.delete<ApiOperationResult>(
      `/api/v1/custom-fields/tables/${tableId}/fields/${fieldId}`
    );
  },
  searchTableRows: async (tableId: number, filter: ApiCFTableRowFilter) => {
    return api.post<ApiPagedData<ApiCFEmployeeTableRow>>(
      `/api/v1/custom-fields/tables/${tableId}/rows/search`,
      filter
    );
  },
  exportTableRows: function (tableId: number, filter: ApiCFTableRowFilter & { format: string }) {
    return api.post<BlobPart>(
      `/api/v1/custom-fields/tables/${tableId}/rows/export`,
      filter,
      undefined,
      {
        responseType: "blob",
      }
    );
  },
};
